import { Box, Button, FormHelperText, Link, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { useSnackbar } from "notistack";
import { useEffect, useRef } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikCheckField } from "components/form/FormikCheckField";
import { FormikTextField } from "components/form/FormikTextField";

import useAuth from "hooks/useAuth";
import useIsMountedRef from "hooks/useIsMountedRef";
import { PasswordValidation } from "utils/password-validation";

import { ApiError } from "types/api";

interface ChangePasswordFormState {
  email: string;
  newPassword: string;
  passwordConfirm: string;
  policy: boolean;
  submit?: string;
}

const ChangePasswordFormSchema: Yup.SchemaOf<ChangePasswordFormState> =
  Yup.object({
    email: Yup.string().max(255).required("Email is required"),
    newPassword: PasswordValidation,
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Required"),
    policy: Yup.boolean()
      .oneOf([true], "You must agree to the Terms and Conditions")
      .required(),
    submit: Yup.string(),
  });

export const ChangePasswordForm: FC<{
  email: string;
  oldPassword: string;
  setLoginValues: React.Dispatch<
    React.SetStateAction<{
      email: string;
      password: string;
    }>
  >;
  setChangePassword: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ email, oldPassword, setLoginValues, setChangePassword }) => {
  const isMountedRef = useIsMountedRef();
  const { newPassword } = useAuth();
  const itemsRef = useRef<HTMLInputElement[]>([]);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, 6);
  }, []);

  const handleNewPasswordSubmit = async (
    values: ChangePasswordFormState,
    { setErrors, setSubmitting }: FormikHelpers<ChangePasswordFormState>
  ) => {
    try {
      await newPassword({
        email: values.email,
        new_password: values.newPassword,
        old_password: oldPassword,
      });

      enqueueSnackbar(t("change_password.success"), {
        variant: "success",
      });
      setLoginValues({ email: values.email, password: values.newPassword });
      setChangePassword(false);
    } catch (err: unknown) {
      if (isMountedRef.current) {
        const error = err as AxiosError<ApiError>;
        setErrors({ submit: error.response?.data.message ?? error.message });
        setSubmitting(false);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        email,
        oldPassword: "",
        newPassword: "",
        policy: false,
        passwordConfirm: "",
      }}
      validationSchema={ChangePasswordFormSchema}
      onSubmit={handleNewPasswordSubmit}
    >
      {({ errors, isSubmitting, handleChange, values }) => (
        <Form>
          <FormikTextField
            name="email"
            autoFocus
            autoComplete="email"
            fullWidth
            label={t("change_password.email")}
            margin="normal"
            type="text"
          />
          <FormikTextField
            name="newPassword"
            fullWidth
            autoComplete="new-password"
            label={t("change_password.new_password")}
            margin="normal"
            type="password"
          />

          <FormikTextField
            name="passwordConfirm"
            fullWidth
            autoComplete="new-password"
            label={t("change_password.new_password_confirm")}
            margin="normal"
            type="password"
          />

          <FormikCheckField
            checked={values.policy}
            color="primary"
            name="policy"
            onChange={handleChange}
            label={
              <Typography color="textSecondary" variant="body2">
                I agree to the{" "}
                <Link
                  color="primary"
                  component="a"
                  href="https://privacyhorizon.com/terms-and-conditions/"
                  target="_blank"
                >
                  Terms and Conditions
                </Link>{" "}
                and{" "}
                <Link
                  color="primary"
                  component="a"
                  href="https://privacyhorizon.com/privacy-notice/"
                  target="_blank"
                >
                  Privacy Notice
                </Link>
              </Typography>
            }
          />

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              color="secondary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {t("change_password.submit")}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
